import React from 'react';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Search, Upload, History, Shield, Settings, 
  Bell, Plus, TrendingUp, AlertTriangle, CheckCircle, Clock,
  FileText, ChevronRight, User
} from 'lucide-react';

const screenings = [
  { name: "Acme Maritime Ltd", date: "08/09/2026", type: "Entity", status: "Clear" },
  { name: "Acme Maritime Ltd", date: "08/09/2026", type: "Entity", matches: 2, status: "High Risk" },
  { name: "Acme Maritime Ltd", date: "08/09/2026", type: "Entity", matches: 4, status: "High Risk" },
  { name: "Acme Maritime Ltd", date: "08/09/2026", type: "Entity", status: "Low Risk" },
  { name: "Acme Maritime Ltd", date: "08/08/2026", type: "Entity", status: "Clear" },
  { name: "Blue Ocean Logistics LLC", date: "08/08/2026", type: "Entity", status: "Clear" },
  { name: "Blue Ocean Logistics LLC", date: "08/06/2026", type: "Entity", status: "Clear" },
  { name: "Blue Ocean Logistics LLC", date: "08/06/2026", type: "Entity", status: "Low Risk" },
  { name: "Blue Ocean Logistics LLC", date: "08/06/2026", type: "Entity", status: "Low Risk" },
  { name: "Pacific Industrial Holdings Corp", date: "05/03/2026", type: "Entity", matches: 1, status: "High Risk" },
];

const navItems = [
  { name: "Dashboard", icon: LayoutDashboard, active: true },
  { name: "New Screening", icon: Search },
  { name: "Bulk Screening", icon: Upload },
  { name: "History", icon: History },
  { name: "Tenant Admin", icon: Shield },
  { name: "Admin", icon: Settings },
];

function StatusBadge({ status, matches }) {
  const styles = {
    "Clear": "bg-green-100 text-green-700",
    "High Risk": "bg-red-100 text-red-700",
    "Low Risk": "bg-blue-100 text-blue-700",
    "Medium Risk": "bg-yellow-100 text-yellow-700",
  };
  return (
    <div className="flex items-center gap-1.5">
      {matches && (
        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
          {matches} matches
        </span>
      )}
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${styles[status] || ""}`}>
        {status}
      </span>
    </div>
  );
}

export default function DashboardMockup({ delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="rounded-xl border border-slate-200 shadow-2xl overflow-hidden bg-white"
    >
      {/* Browser Chrome */}
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border-b border-slate-200">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 mx-4 px-3 py-1 rounded bg-white border border-slate-200 text-[11px] text-slate-400 text-center">
          wc360.base44.app/Dashboard
        </div>
      </div>

      {/* App Nav Bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white font-bold text-xs">W</div>
            <span className="text-[11px] font-bold text-slate-800 tracking-tight">WORLD CHECK 360°</span>
          </div>
          <nav className="hidden md:flex items-center gap-0.5">
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <div key={item.name} className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium ${item.active ? "bg-rose-500 text-white" : "text-slate-500"}`}>
                  <Icon className="w-3 h-3" />
                  {item.name}
                </div>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <Bell className="w-3.5 h-3.5 text-slate-400" />
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-[10px] font-medium">J</div>
            <div className="hidden sm:block">
              <div className="text-[10px] font-medium text-slate-800 leading-none">John Carter</div>
              <div className="text-[9px] text-slate-400 leading-none mt-0.5">Admin</div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="p-4 bg-slate-50">
        {/* Title + Search */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Compliance Dashboard</h3>
            <p className="text-[10px] text-slate-500">Overview of screening activity and risk metrics</p>
          </div>
          <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-rose-500 text-white text-[10px] font-medium shadow-sm">
            <Plus className="w-3 h-3" /> New Screening
          </button>
        </div>
        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            readOnly
            placeholder="Search by name, IMO number, or reference ID..."
            className="w-full pl-8 pr-3 py-2 text-[11px] border border-slate-200 rounded-md bg-white text-slate-400"
          />
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { label: "Total Screenings", value: "41", sub: "All time", color: "bg-blue-500", icon: FileText },
            { label: "High Risk Matches", value: "17", sub: "Requires attention", color: "bg-red-500", icon: AlertTriangle },
            { label: "Clear Screenings", value: "20", sub: "No matches found", color: "bg-green-500", icon: CheckCircle },
            { label: "Pending Review", value: "3", sub: "In progress", color: "bg-orange-500", icon: Clock },
          ].map(stat => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="bg-white rounded-lg border border-slate-200 p-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <div className={`w-7 h-7 rounded ${stat.color} flex items-center justify-center`}>
                    <Icon className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
                <div className="text-lg font-bold text-slate-900 leading-none">{stat.value}</div>
                <div className="text-[9px] text-slate-500 mt-0.5">{stat.label}</div>
                <div className="text-[8px] text-slate-400 mt-0.5">{stat.sub}</div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {/* Left: Chart + Recent Screenings */}
          <div className="col-span-2 space-y-3">
            {/* Trend Chart */}
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[11px] font-semibold text-slate-700">Screening Trends</h4>
                <span className="text-[9px] text-slate-400">4 / Last 7 days</span>
              </div>
              <svg viewBox="0 0 300 80" className="w-full h-16">
                <defs>
                  <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M0,60 L40,55 L80,58 L120,50 L160,45 L200,15 L240,48 L280,52 L300,50 L300,80 L0,80 Z" fill="url(#chartGrad)" />
                <path d="M0,60 L40,55 L80,58 L120,50 L160,45 L200,15 L240,48 L280,52 L300,50" fill="none" stroke="#f43f5e" strokeWidth="1.5" />
                <circle cx="200" cy="15" r="3" fill="#f43f5e" />
              </svg>
              <div className="flex items-center gap-1.5 mt-2 px-2 py-1 rounded bg-red-50">
                <AlertTriangle className="w-3 h-3 text-red-500" />
                <span className="text-[9px] text-red-600 font-medium">2 high risk matches detected this week</span>
              </div>
            </div>

            {/* Recent Screenings */}
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                <h4 className="text-[11px] font-semibold text-slate-700">Recent Screenings</h4>
                <span className="text-[9px] text-rose-500 font-medium">View All</span>
              </div>
              <div className="divide-y divide-slate-50">
                {screenings.map((s, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 hover:bg-slate-50">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-3 h-3 text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] font-medium text-slate-800 truncate">{s.name}</div>
                        <div className="text-[8px] text-slate-400">{s.date} · {s.type}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <StatusBadge status={s.status} matches={s.matches} />
                      <ChevronRight className="w-3 h-3 text-slate-300" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Risk Distribution + Quick Actions */}
          <div className="space-y-3">
            {/* Risk Distribution */}
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <h4 className="text-[11px] font-semibold text-slate-700 mb-2.5">Risk Distribution</h4>
              <div className="space-y-2">
                {[
                  { label: "High Risk", count: 17, color: "bg-red-500", pct: "85%" },
                  { label: "Medium Risk", count: 1, color: "bg-yellow-500", pct: "5%" },
                  { label: "Low Risk", count: 0, color: "bg-blue-500", pct: "0%" },
                  { label: "Clear", count: 20, color: "bg-green-500", pct: "10%" },
                ].map(r => (
                  <div key={r.label}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[9px] text-slate-600">{r.label}</span>
                      <span className="text-[9px] font-medium text-slate-700">{r.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full ${r.color}`} style={{ width: r.pct }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <h4 className="text-[11px] font-semibold text-slate-700 mb-2.5">Quick Actions</h4>
              <div className="space-y-1.5">
                {[
                  { label: "Screen Individual", icon: Search },
                  { label: "Screen Entity", icon: Search },
                  { label: "Bulk Screening", icon: Upload },
                  { label: "View History", icon: History },
                ].map(a => {
                  const Icon = a.icon;
                  return (
                    <div key={a.label} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer">
                      <div className="w-6 h-6 rounded bg-rose-50 flex items-center justify-center">
                        <Icon className="w-3 h-3 text-rose-500" />
                      </div>
                      <span className="text-[10px] text-slate-600 font-medium">{a.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}